import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { NO_GRANTS, VENUE_MANAGER_DEFAULTS, type PermissionArea } from '@lib/constants/permissions'
import { VENUE_MANAGER_BUILT_IN, type Grants, type TenantRoleDto } from '@lib/dto/tenant-role.dto'
import type { TenantFeature } from '@lib/enums/tenant-feature.enum'
import type { TenantRoleReach } from '@lib/enums/tenant-role-reach.enum'
import { tenantRoleInputSchema } from '@lib/input/tenant-role.input'
import { PermissionTable } from '@/components/roles/PermissionTable'
import { areasFor, peopleLine, REACH_HINT, REACH_LABEL, withLevel, type PermissionLevel } from '@/components/roles/roleText'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { ChoiceCard, ChoiceGroup } from '@/components/ui/choice-card'
import { Field } from '@/components/ui/field'
import { FormActions } from '@/components/ui/form-actions'
import { Input } from '@/components/ui/input'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { isApiError, requestErrorMessage } from '@/helpers/apiClient'
import { useOrganization } from '@/hooks/useOrganization'
import { useCreateTenantRole, useTenantRole, useUpdateTenantRole } from '@/hooks/useTenantRoles'

/** Assigned venues first: it is where a new role starts, and the narrower of the two. */
const REACH_ORDER: readonly TenantRoleReach[] = ['venues', 'company']

/** A role, new or existing (docs/custom-roles.spec.md, "Roles" and "Permissions"). Organization admins only. */
export function RoleEditorPage() {
  const { roleId } = useParams()
  const navigate = useNavigate()
  const isNew = roleId === undefined
  const roleQuery = useTenantRole(roleId ?? '', !isNew)
  const organizationQuery = useOrganization()

  if ((!isNew && roleQuery.isPending) || organizationQuery.isPending) {
    return (
      <PageContainer>
        <DetailSkeleton />
      </PageContainer>
    )
  }
  if (!isNew && roleQuery.isError) {
    return (
      <PageContainer>
        <ErrorState message={requestErrorMessage(roleQuery.error)} onRetry={() => navigate('/roles')} />
      </PageContainer>
    )
  }
  const role = isNew ? null : (roleQuery.data ?? null)
  // Keyed, so moving from one role to another starts its form afresh.
  return <RoleForm key={role?.id ?? 'new'} role={role} features={organizationQuery.data?.features ?? []} />
}

interface RoleFormProps {
  /** Null for a new role. */
  role: TenantRoleDto | null
  features: readonly TenantFeature[]
}

function RoleForm({ role, features }: RoleFormProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const create = useCreateTenantRole()
  const update = useUpdateTenantRole(role?.id ?? '')
  const [name, setName] = React.useState(role?.name ?? '')
  const [reach, setReach] = React.useState<TenantRoleReach>(role?.reach ?? 'venues')
  const [grants, setGrants] = React.useState<Grants>(role?.grants ?? NO_GRANTS)
  const [nameError, setNameError] = React.useState<string | null>(null)
  // Save is at the foot of a long form, so a problem with the name, at its head,
  // brings the field back into view.
  const nameRef = React.useRef<HTMLInputElement>(null)
  const areas = areasFor(features)
  const saving = create.isPending || update.isPending
  // Narrowing a company-wide role leaves its people without venues until each is given some.
  const strands = role !== null && role.reach === 'company' && reach === 'venues' && role.people > 0
  // What applies at every venue isn't any one venue's to change (api `requireCompanyIntegrations`, Prism imports).
  const companyOnly = reach === 'venues' && (grants.integrations === 'edit' || grants.imports === 'edit')

  function setLevel(key: PermissionArea, level: PermissionLevel) {
    setGrants((current) => withLevel(current, key, level))
  }

  function applyDefaults() {
    setReach('venues')
    setGrants(VENUE_MANAGER_DEFAULTS)
  }

  function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    const parsed = tenantRoleInputSchema.safeParse({ name, reach, grants })
    if (!parsed.success) {
      const issue = parsed.error.issues[0]
      if (issue?.path[0] === 'name') showNameError(issue.message)
      else toast(issue?.message ?? 'Check the role and try again.', { variant: 'error' })
      return
    }
    setNameError(null)
    const done = { onSuccess: () => navigate('/roles'), onError: onSaveError }
    if (role === null) create.mutate(parsed.data, done)
    else update.mutate(parsed.data, done)
  }

  function onSaveError(error: Error) {
    // A taken name is the name's problem, so it is said there.
    if (isApiError(error) && error.status === 409) showNameError(requestErrorMessage(error))
    else toast(requestErrorMessage(error), { variant: 'error' })
  }

  function showNameError(message: string) {
    setNameError(message)
    nameRef.current?.focus()
  }

  return (
    <PageContainer className="space-y-6">
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => navigate('/roles')}
          className="inline-flex items-center gap-1.5 text-sm text-ink-muted transition-colors hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" /> Roles
        </button>
        <PageHeader
          title={role === null ? 'New role' : role.name}
          meta={role === null ? 'Choose where people in this role work and what they can do.' : peopleLine(role.people)}
        />
      </div>

      <form onSubmit={onSubmit} className="space-y-6" noValidate>
        <Field label="Name" htmlFor="role-name" error={nameError}>
          <Input
            ref={nameRef}
            id="role-name"
            value={name}
            maxLength={60}
            placeholder="Sales manager"
            onChange={(e) => {
              setName(e.target.value)
              setNameError(null)
            }}
          />
        </Field>

        <ChoiceGroup legend="Works at" columns={2}>
          {REACH_ORDER.map((option) => (
            <ChoiceCard
              key={option}
              type="radio"
              name="reach"
              checked={reach === option}
              onSelect={() => setReach(option)}
              title={REACH_LABEL[option]}
              description={REACH_HINT[option]}
            />
          ))}
        </ChoiceGroup>
        {strands ? (
          <Callout tone="attention" title={peopleLine(role.people)}>
            They’ll have no venues until each is assigned some.
          </Callout>
        ) : null}

        <PermissionTable
          heading={reach === 'company' ? 'At every venue' : 'At their venues'}
          areas={areas.filter((area) => area.scope === 'venue')}
          grants={grants}
          onLevel={setLevel}
          onSwitch={(key, allowed) => setGrants((current) => ({ ...current, [key]: allowed }))}
        />
        <PermissionTable
          heading="Across the company"
          areas={areas.filter((area) => area.scope === 'company')}
          grants={grants}
          onLevel={setLevel}
          onSwitch={(key, allowed) => setGrants((current) => ({ ...current, [key]: allowed }))}
        />
        {companyOnly ? (
          <Callout title="Some of this needs Every venue">
            {grants.integrations === 'edit'
              ? 'At their venues, people in this role connect GoHighLevel and refresh lead stats. Google Ads, the company’s GoHighLevel settings and what lead stats count apply at every venue, so those need a role that works at every venue. '
              : null}
            {grants.imports === 'edit' ? 'An import can fill any venue, so imports need a role that works at every venue.' : null}
          </Callout>
        ) : null}

        <FormActions>
          {role?.builtIn === VENUE_MANAGER_BUILT_IN ? (
            <Button type="button" variant="outline" onClick={applyDefaults} className="mr-auto">
              Use the defaults
            </Button>
          ) : null}
          <Button type="button" variant="ghost" onClick={() => navigate('/roles')}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {role === null ? 'Create role' : 'Save role'}
          </Button>
        </FormActions>
      </form>
    </PageContainer>
  )
}
